import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  type ScriptableScalePointLabelContext,
} from 'chart.js';
import { useTheme } from '../lib/useTheme';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface Props {
  labels: string[];
  values: number[];
  // WHY: When provided, tints each point dot + axis label by its ability's
  // pastel color (skills/saving-throws radar). When absent, the radar falls
  // back to a single neutral accent (legacy behavior).
  pointColors?: string[];
}

// WHY: Chart.js draws on a <canvas>, which does NOT resolve CSS var() — colors
// must be literal. Two SANSARA palettes keep the chart legible on each surface:
// the dark palette on the near-black violet surface, the light palette on the
// clean near-white surface. Accent is the brand violet/lilac pair.
const PALETTE = {
  dark: {
    accent: 'rgba(217, 70, 239, 1)',
    accentFill: 'rgba(217, 70, 239, 0.2)',
    grid: 'rgba(42, 29, 77, 0.6)',
    text: '#d9d2ea',
    textMuted: '#a394c4',
    pointBorder: '#ffffff',
    pointHoverBg: '#ffffff',
  },
  light: {
    accent: 'rgba(124, 34, 206, 1)',
    accentFill: 'rgba(124, 34, 206, 0.15)',
    grid: 'rgba(226, 232, 240, 0.9)',
    text: '#0f172a',
    textMuted: '#64748b',
    pointBorder: '#0f172a',
    pointHoverBg: '#0f172a',
  },
} as const;

// WHY: Chart.js draws on a <canvas> and does NOT inherit the body font, so the
// Source Sans Pro family is set explicitly to match the app's UI font.
const FONT_FAMILY = "'Source Sans Pro', system-ui, sans-serif";
const FONT_SIZE = 11;
const FONT_SIZE_SM = 10;

export default function PartyRadar({ labels, values, pointColors }: Props) {
  const { theme } = useTheme();
  const C = PALETTE[theme];
  const pointBackgroundColor = pointColors ?? C.accent;
  const pointHoverBackgroundColor = pointColors ?? C.pointHoverBg;

  const chartData = {
    labels,
    datasets: [
      {
        label: '',
        data: values,
        backgroundColor: C.accentFill,
        borderColor: C.accent,
        borderWidth: 2,
        pointBackgroundColor,
        pointBorderColor: C.pointBorder,
        pointHoverBackgroundColor,
        pointHoverBorderColor: C.accent,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    // WHY: The radar's caption/title is no longer rendered by the parent, so we drop
    // Chart.js's in-canvas legend entirely to give the radar more vertical space.
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      r: {
        angleLines: {
          color: C.grid,
        },
        grid: {
          color: C.grid,
        },
        pointLabels: {
          // WHY: Chart.js types reject a raw color array here, but a scriptable
          // indexed into pointColors gives the same per-axis tint and is typed.
          color: pointColors
            ? (ctx: ScriptableScalePointLabelContext) => pointColors[ctx.index ?? 0]
            : C.text,
          font: {
            size: FONT_SIZE,
            family: FONT_FAMILY,
          },
        },
        ticks: {
          color: C.textMuted,
          backdropColor: 'transparent',
          font: {
            size: FONT_SIZE_SM,
            family: FONT_FAMILY,
          },
        },
      },
    },
  };

  return (
    <div className="max-w-lg mx-auto">
      <Radar data={chartData} options={options} />
    </div>
  );
}
